import { useState, useMemo } from "react";
import {
  Plus, Loader2, Pencil, Trash2, Truck, BarChart3, List, Search, Eye,
  Ban, CheckCircle2, KeyRound, ChevronLeft, ChevronRight, Download,
} from "lucide-react";
import toast from "react-hot-toast";
import { useDrivers } from "../hooks/useDrivers";
import { useDriverScorecards } from "../hooks/useDriverScorecards";
import { useAuth } from "../context/AuthContext";
import DriverFormModal from "./DriverFormModal";
import DriverDetailsPage from "./DriverDetailsPage";

const PAGE_SIZE = 10;

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function expiryStatus(iso) {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0)  return { label: "Expired",              color: "#DC2626", bg: "#FEF2F2" };
  if (days <= 30) return { label: `Expires in ${days}d`,  color: "#D97706", bg: "#FFFBEB" };
  return { label: `Valid till ${fmtDate(iso)}`,           color: "#16A34A", bg: "#F0FDF4" };
}

function exportCsv(drivers) {
  const headers = ["Name", "Employee ID", "Email", "Phone", "Assigned Vehicle", "License Number", "License Expiry", "Status", "Created Date"];
  const rows = drivers.map(d => [
    d.name, d.employeeId || "", d.email || "", d.phone || "", d.assignedVehiclePlate || "",
    d.licenseNumber || "", d.licenseExpiry ? d.licenseExpiry.slice(0, 10) : "", d.status, d.createdAt ? d.createdAt.slice(0, 10) : "",
  ]);
  const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = "drivers.csv";
  document.body.appendChild(link); link.click(); link.remove();
  URL.revokeObjectURL(url);
}

export default function DriverPanel({ vehicles }) {
  const { isFleet } = useAuth();
  const {
    drivers, loading, addDriver, updateDriver, removeDriver, setDriverVehicle,
    deactivateDriver, reactivateDriver, resetPassword,
  } = useDrivers();
  const [view,          setView]          = useState("roster"); // 'roster' | 'scorecard'
  const { scorecards, loading: scoresLoading } = useDriverScorecards(isFleet && view === "scorecard");
  const [showForm,      setShowForm]      = useState(false);
  const [editingDriver, setEditingDriver]  = useState(null);
  const [profileDriverId, setProfileDriverId] = useState(null);

  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive | assigned | unassigned
  const [page,          setPage]          = useState(1);

  function openAdd()  { setEditingDriver(null); setShowForm(true); }
  function openEdit(d) { setEditingDriver(d);   setShowForm(true); }

  async function handleSubmit(payload) {
    if (editingDriver) await updateDriver(editingDriver.id, payload);
    else                await addDriver(payload);
  }

  async function handleDelete(driver) {
    if (!window.confirm(`Remove ${driver.name} from your drivers? This can't be undone.`)) return;
    try {
      await removeDriver(driver.id);
      toast.success("Driver removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove driver");
    }
  }

  async function handleAssign(driver, vehicleId) {
    try {
      await setDriverVehicle(driver.id, vehicleId || null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update assignment");
    }
  }

  async function handleToggleActive(driver) {
    try {
      if (driver.status === "active") { await deactivateDriver(driver.id); toast.success("Driver deactivated"); }
      else                              { await reactivateDriver(driver.id); toast.success("Driver reactivated"); }
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    }
  }

  async function handleQuickResetPassword(driver) {
    if (!driver.loginEmail) return toast.error("This driver has no Driver Portal login yet");
    try {
      const result = await resetPassword(driver.id, true);
      toast.success(`New temporary password: ${result.tempPassword}`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset password");
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers.filter(d => {
      if (statusFilter === "active" && d.status !== "active") return false;
      if (statusFilter === "inactive" && d.status !== "inactive") return false;
      if (statusFilter === "assigned" && !d.assignedVehicleId) return false;
      if (statusFilter === "unassigned" && d.assignedVehicleId) return false;
      if (!q) return true;
      return [d.name, d.employeeId, d.email, d.phone, d.licenseNumber].some(v => v?.toLowerCase().includes(q));
    });
  }, [drivers, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Driver Management</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>
              {view === "roster" ? "Manage your drivers, credentials, and vehicle assignments" : "Trip performance derived from each driver's history"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isFleet && (
              <div style={{ display: "flex", background: "#F3F4F6", borderRadius: 8, padding: 3 }}>
                <button
                  onClick={() => setView("roster")}
                  style={{
                    display: "flex", alignItems: "center", gap: 5, border: "none", borderRadius: 6,
                    padding: "6px 12px", cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                    background: view === "roster" ? "#fff" : "transparent",
                    color: view === "roster" ? "#111827" : "#6B7280",
                    boxShadow: view === "roster" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                ><List size={13} /> Roster</button>
                <button
                  onClick={() => setView("scorecard")}
                  style={{
                    display: "flex", alignItems: "center", gap: 5, border: "none", borderRadius: 6,
                    padding: "6px 12px", cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                    background: view === "scorecard" ? "#fff" : "transparent",
                    color: view === "scorecard" ? "#111827" : "#6B7280",
                    boxShadow: view === "scorecard" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                ><BarChart3 size={13} /> Scorecard</button>
              </div>
            )}
            {view === "roster" && (
              <button
                onClick={openAdd}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "#4F46E5", border: "none", borderRadius: 8,
                  padding: "0 16px", height: 38, cursor: "pointer",
                  fontSize: 13.5, fontWeight: 700, color: "#fff",
                }}
              >
                <Plus size={15} /> Add Driver
              </button>
            )}
          </div>
        </div>

        {view === "scorecard" ? (
          <ScorecardTable scorecards={scorecards} loading={scoresLoading} />
        ) : (
          <>
            {/* Search / filter / export toolbar */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ position: "relative", flex: "1 1 240px" }}>
                <Search size={14} color="#9CA3AF" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                <input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search name, employee ID, email, phone, license…"
                  style={{
                    width: "100%", boxSizing: "border-box", height: 38, paddingLeft: 32, paddingRight: 10,
                    borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13,
                  }}
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                style={{ height: 38, borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, padding: "0 10px", cursor: "pointer" }}
              >
                <option value="all">All Drivers</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="assigned">Assigned Drivers</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <button
                onClick={() => exportCsv(filtered)}
                style={{
                  display: "flex", alignItems: "center", gap: 6, height: 38, padding: "0 14px",
                  borderRadius: 8, border: "1px solid #E5E7EB", background: "#fff", fontSize: 13, fontWeight: 600,
                  color: "#374151", cursor: "pointer",
                }}
              ><Download size={13} /> Export</button>
            </div>

            {loading && (
              <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading drivers…
              </div>
            )}

            {!loading && filtered.length === 0 && (
              <div style={{ background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14, padding: "48px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  {drivers.length === 0 ? "No drivers yet" : "No drivers match your search"}
                </div>
                {drivers.length === 0 && (
                  <>
                    <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>Add your first driver to start assigning them to vehicles.</div>
                    <button onClick={openAdd} style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                      <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Add Driver
                    </button>
                  </>
                )}
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#F9FAFB" }}>
                      {["Driver", "Employee ID", "Email", "Phone", "Assigned Vehicle", "License", "Status", "Created", "Actions"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.3, borderBottom: "1px solid #E5E7EB", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map(driver => {
                      const expiry = expiryStatus(driver.licenseExpiry);
                      return (
                        <tr key={driver.id} style={{ borderBottom: "1px solid #F3F4F6" }}>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#EEF2FF", color: "#4F46E5", fontWeight: 700, fontSize: 12.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                                {driver.photoUrl ? <img src={driver.photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : driver.name.trim().charAt(0).toUpperCase()}
                              </div>
                              <span style={{ fontWeight: 700, color: "#111827" }}>{driver.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: "10px 14px", color: "#6B7280" }}>{driver.employeeId || "—"}</td>
                          <td style={{ padding: "10px 14px", color: "#6B7280" }}>{driver.email || "—"}</td>
                          <td style={{ padding: "10px 14px", color: "#6B7280" }}>{driver.phone || "—"}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <select
                              value={driver.assignedVehicleId || ""}
                              onChange={e => handleAssign(driver, e.target.value)}
                              style={{ height: 30, borderRadius: 6, border: "1px solid #E5E7EB", background: "#F9FAFB", fontSize: 12, color: "#374151", padding: "0 6px", cursor: "pointer" }}
                            >
                              <option value="">— Unassigned —</option>
                              {vehicles.map(v => (
                                <option key={v.id} value={v.id}>
                                  {v.nickname || v.plateNumber}{v.driverId && v.driverId !== driver.id ? " (taken)" : ""}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {expiry
                              ? <span style={{ fontSize: 10.5, fontWeight: 700, color: expiry.color, background: expiry.bg, borderRadius: 99, padding: "2px 8px" }}>{expiry.label}</span>
                              : <span style={{ color: "#9CA3AF" }}>—</span>}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "capitalize", color: driver.status === "active" ? "#16A34A" : "#9CA3AF", background: driver.status === "active" ? "#F0FDF4" : "#F3F4F6", borderRadius: 99, padding: "2px 8px" }}>
                              {driver.status}
                            </span>
                          </td>
                          <td style={{ padding: "10px 14px", color: "#9CA3AF", whiteSpace: "nowrap" }}>{fmtDate(driver.createdAt)}</td>
                          <td style={{ padding: "10px 14px" }}>
                            <div style={{ display: "flex", gap: 4 }}>
                              <IconButton icon={Eye} title="View" onClick={() => setProfileDriverId(driver.id)} />
                              <IconButton icon={Pencil} title="Edit" onClick={() => openEdit(driver)} />
                              <IconButton icon={KeyRound} title="Reset Password" onClick={() => handleQuickResetPassword(driver)} />
                              {driver.status === "active"
                                ? <IconButton icon={Ban} title="Deactivate" color="#D97706" onClick={() => handleToggleActive(driver)} />
                                : <IconButton icon={CheckCircle2} title="Reactivate" color="#16A34A" onClick={() => handleToggleActive(driver)} />}
                              <IconButton icon={Trash2} title="Delete" color="#DC2626" onClick={() => handleDelete(driver)} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 14 }}>
                <IconButton icon={ChevronLeft} title="Previous" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pageSafe === 1} />
                <span style={{ fontSize: 13, color: "#6B7280" }}>Page {pageSafe} of {totalPages}</span>
                <IconButton icon={ChevronRight} title="Next" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={pageSafe === totalPages} />
              </div>
            )}
          </>
        )}
      </div>

      {showForm && (
        <DriverFormModal driver={editingDriver} onClose={() => setShowForm(false)} onSubmit={handleSubmit} />
      )}
      {profileDriverId && (
        <DriverDetailsPage driverId={profileDriverId} onClose={() => setProfileDriverId(null)} />
      )}
    </main>
  );
}

function IconButton({ icon: Icon, title, onClick, color = "#6B7280", disabled }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      style={{
        background: "none", border: "1px solid #E5E7EB", borderRadius: 7, width: 30, height: 30,
        cursor: disabled ? "default" : "pointer", color, display: "flex", alignItems: "center", justifyContent: "center",
        opacity: disabled ? 0.4 : 1,
      }}
    ><Icon size={13} /></button>
  );
}

function ScorecardTable({ scorecards, loading }) {
  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading scorecards…
      </div>
    );
  }

  if (scorecards.length === 0) {
    return (
      <div style={{
        background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
        padding: "48px 24px", textAlign: "center",
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No drivers yet</div>
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>Scorecards appear once drivers have trips assigned to them.</div>
      </div>
    );
  }

  const cols = ["Driver", "Trips", "Completed", "Cancelled", "Completion", "Distance", "Avg Duration"];

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#F9FAFB" }}>
            {cols.map(c => (
              <th key={c} style={{ textAlign: "left", padding: "10px 14px", fontSize: 11.5, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, borderBottom: "1px solid #E5E7EB" }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scorecards.map(s => (
            <tr key={s.driverId} style={{ borderBottom: "1px solid #F3F4F6" }}>
              <td style={{ padding: "12px 14px", fontWeight: 700, color: "#111827" }}>{s.name}</td>
              <td style={{ padding: "12px 14px", color: "#374151" }}>{s.totalTrips}</td>
              <td style={{ padding: "12px 14px", color: "#16A34A", fontWeight: 600 }}>{s.completedTrips}</td>
              <td style={{ padding: "12px 14px", color: "#DC2626", fontWeight: 600 }}>{s.cancelledTrips}</td>
              <td style={{ padding: "12px 14px", color: "#374151" }}>{s.completionRate != null ? `${s.completionRate}%` : "—"}</td>
              <td style={{ padding: "12px 14px", color: "#374151" }}>{s.totalDistanceKm} km</td>
              <td style={{ padding: "12px 14px", color: "#374151" }}>{s.avgTripDurationMins != null ? `${s.avgTripDurationMins} min` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
